import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UploadCloud, Loader2, ArrowLeft, CheckCircle2, FolderArchive,
  Files, Folder, X, AlertTriangle, Eye, EyeOff, ListChecks, Wand2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { PageHero } from "@/components/PageHero";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TIPI_DOCUMENTO } from "@/lib/constants";
import { toast } from "sonner";

const SUPPORTED = ["pdf", "docx", "txt", "xlsx", "zip"];
const ext = (n) => (n.includes(".") ? n.split(".").pop().toLowerCase() : "");
const isSupported = (f) => SUPPORTED.includes(ext(f.name));
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const EXT_BADGE = {
  xlsx: "text-green-700 border-green-300 bg-green-50",
  docx: "text-blue-700 border-blue-300 bg-blue-50",
  pdf: "text-red-700 border-red-300 bg-red-50",
  txt: "text-slate-600 border-slate-300 bg-slate-50",
};

export default function BulkImport() {
  const navigate = useNavigate();
  const { tenantId, organizations } = useTenant();
  const [localTenant, setLocalTenant] = useState(tenantId || "");
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle | preparing | analyzing | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const folderInput = useRef(null);
  const active = localTenant || tenantId;

  // Bulk-assign panel state
  const [schemes, setSchemes] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyProcs, setApplyProcs] = useState([]);
  const [applyScheme, setApplyScheme] = useState("");
  const [applyReqs, setApplyReqs] = useState([]);

  useEffect(() => { if (tenantId) setLocalTenant(tenantId); }, [tenantId]);

  useEffect(() => {
    const el = folderInput.current;
    if (el) { el.setAttribute("webkitdirectory", ""); el.setAttribute("directory", ""); el.setAttribute("mozdirectory", ""); }
  }, []);

  useEffect(() => { api.get("/schemes").then((r) => setSchemes(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (active) api.get(`/processes?tenant_id=${active}`).then((r) => setProcesses(r.data)).catch(() => setProcesses([]));
  }, [active]);
  useEffect(() => {
    if (applyScheme) api.get(`/requirements?scheme_id=${applyScheme}`).then((r) => setRequirements(r.data)).catch(() => setRequirements([]));
    else setRequirements([]);
  }, [applyScheme]);

  const fileKey = (f) => (f.webkitRelativePath || f.name) + "|" + f.size;

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter(isSupported);
    if (!incoming.length) { toast.warning("Nessun file supportato (PDF, DOCX, TXT, ZIP)"); return; }
    setFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      const merged = [...prev];
      incoming.forEach((f) => { if (!seen.has(fileKey(f))) { merged.push(f); seen.add(fileKey(f)); } });
      return merged;
    });
  };

  const removeFile = (i) => setFiles((arr) => arr.filter((_, idx) => idx !== i));

  const buildOk = (filename, file, data) => {
    const s = data.suggestions || {};
    return {
      filename, file, suggestions: s, text_excerpt: data.text_excerpt || "",
      duplicate: data.duplicate, text_found: data.text_found, error: false,
      include: !data.duplicate, dupAction: data.duplicate ? "skip" : "create",
      codice: s.codice_documento || "",
      titolo: s.titolo || filename.replace(/\.[^.]+$/, ""),
      tipo: TIPI_DOCUMENTO.includes(s.tipo) ? s.tipo : "PG",
      scheme_id: s.suggested_scheme_id || null,
      process_ids: s.suggested_process_ids || [],
      requirement_ids: s.suggested_requirement_ids || [],
    };
  };
  const buildErr = (filename, file) => ({
    filename, file, suggestions: {}, text_excerpt: "", duplicate: false,
    error: true, include: false, dupAction: "create",
    codice: "", titolo: filename.replace(/\.[^.]+$/, ""), tipo: "PG",
    scheme_id: null, process_ids: [], requirement_ids: [],
  });
  const extractEntry = async (en, attempts = 3) => {
    let lastErr;
    for (let a = 0; a < attempts; a++) {
      try {
        const r = await api.post("/documents/extract-entry", {
          tenant_id: active, storage_path: en.file.storage_path, filename: en.filename,
        });
        return r.data;
      } catch (e) {
        lastErr = e;
        if (a < attempts - 1) await new Promise((res) => setTimeout(res, 800 * (a + 1)));
      }
    }
    throw lastErr;
  };
  const retryItem = async (i) => {
    const it = items[i];
    setRetrying(i);
    try {
      const data = await extractEntry({ filename: it.filename, file: it.file });
      setItems((arr) => arr.map((x, idx) => (idx === i ? buildOk(it.filename, it.file, data) : x)));
      toast.success("Documento rianalizzato");
    } catch (e) {
      toast.error("Riprova non riuscita");
    } finally {
      setRetrying(null);
    }
  };

  const analyze = async () => {
    if (!active) { toast.error("Seleziona un cliente"); return; }
    if (!files.length) { toast.error("Seleziona file, una cartella o un .zip"); return; }
    setItems([]); setSkipped([]); setPreviewIdx(null);
    setPhase("preparing");
    setProgress({ done: 0, total: 0 });
    try {
      const fd = new FormData();
      fd.append("tenant_id", active);
      files.forEach((f) => fd.append("files", f));
      const prep = await api.post("/documents/bulk-prepare", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const entries = prep.data.entries || [];
      const sk = prep.data.skipped || [];
      if (prep.data.dropped) sk.push({ filename: `+${prep.data.dropped} file`, reason: "superato il limite di 200 per import" });
      setSkipped(sk);
      if (!entries.length) {
        toast.warning("Nessun documento supportato trovato");
        setPhase("idle"); return;
      }

      setPhase("analyzing");
      setProgress({ done: 0, total: entries.length });
      const results = [];
      let done = 0;
      const queue = entries.slice();
      const worker = async () => {
        while (queue.length) {
          const en = queue.shift();
          try {
            results.push(buildOk(en.filename, en.file, await extractEntry(en)));
          } catch (e) {
            results.push(buildErr(en.filename, en.file));
          }
          done++;
          setProgress({ done, total: entries.length });
          setItems([...results]);
        }
      };
      await Promise.all(Array.from({ length: Math.min(5, entries.length) }, worker));
      setPhase("done");
      const errs = results.filter((r) => r.error).length;
      if (errs) toast.warning(`${results.length} analizzati, ${errs} con errori`);
      else toast.success(`${results.length} documenti analizzati`);
      if (sk.length) toast.warning(`${sk.length} file scartati (non supportati o corrotti)`);
    } catch (e) {
      toast.error(apiError(e));
      setPhase("idle");
    }
  };

  const upd = (i, patch) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const toggleIn = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const toSave = items.filter((it) => it.include && !it.error && !(it.duplicate && it.dupAction === "skip"));

  const applyToAll = () => {
    if (!toSave.length) { toast.error("Nessun documento incluso a cui applicare"); return; }
    const includedIdx = new Set(items.map((it, i) => (toSave.includes(it) ? i : -1)));
    setItems((arr) => arr.map((it, i) => {
      if (!includedIdx.has(i)) return it;
      return {
        ...it,
        process_ids: [...applyProcs],
        ...(applyScheme ? { scheme_id: applyScheme, requirement_ids: [...applyReqs] } : {}),
      };
    }));
    toast.success(`Assegnati a ${toSave.length} documenti`);
    setApplyOpen(false);
  };

  const save = async () => {
    if (!toSave.length) { toast.error("Nessun documento da salvare"); return; }
    setSaving(true);
    let ok = 0, fail = 0;
    for (const it of toSave) {
      const s = it.suggestions;
      try {
        await api.post("/documents", {
          tenant_id: active, codice: it.codice, titolo: it.titolo, tipo: it.tipo,
          descrizione: s.descrizione || "", process_ids: it.process_ids || [],
          requirement_ids: it.requirement_ids || [], scheme_id: it.scheme_id || null,
          versione: s.versione || "1.0", stato: "vigente", file: it.file,
        });
        ok++;
      } catch (e) { fail++; }
    }
    setSaving(false);
    if (fail) { toast.warning(`${ok} salvati, ${fail} non riusciti`); return; }
    toast.success(`${ok} documenti salvati`);
    navigate("/documenti");
  };

  const busy = phase === "preparing" || phase === "analyzing";
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="space-y-6 max-w-5xl">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
      </button>
      <PageHero label="Schedatura massiva" title="Import ZIP / multi-file / cartelle"
        subtitle="Carica più procedure insieme, un'intera cartella o un archivio .zip: l'AI estrae i metadati di ognuna, poi rivedi e salvi in blocco." />

      <Card className="p-6 space-y-5">
        <div>
          <Label>Cliente</Label>
          <Select value={active || ""} onValueChange={setLocalTenant}>
            <SelectTrigger className="mt-1.5 w-full md:w-80" data-testid="bulk-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
            <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block cursor-pointer" data-testid="bulk-dropzone">
            <input type="file" multiple accept=".zip,.pdf,.docx,.xlsx,.txt" className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="bulk-file-input" />
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50/40 transition-colors h-full flex flex-col items-center justify-center">
              <Files className="h-8 w-8 text-slate-500 mb-2" />
              <p className="text-sm font-medium text-slate-700">Seleziona file</p>
              <p className="text-xs text-slate-500 mt-0.5">Più PDF, DOCX, XLSX, TXT o .zip</p>
            </div>
          </label>

          <label className="block cursor-pointer" data-testid="bulk-folder-dropzone">
            <input ref={folderInput} type="file" multiple className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="bulk-folder-input" />
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors h-full flex flex-col items-center justify-center">
              <Folder className="h-8 w-8 text-slate-500 mb-2" />
              <p className="text-sm font-medium text-slate-700">Seleziona una cartella</p>
              <p className="text-xs text-slate-500 mt-0.5">Aggiungi più cartelle ripetendo la selezione</p>
            </div>
          </label>
        </div>

        {files.length > 0 && (
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="bulk-selected-list">
            <div className="px-3 py-2 flex items-center justify-between bg-slate-50/60">
              <span className="text-xs font-medium text-slate-600">{files.length} file selezionati</span>
              <button onClick={() => setFiles([])} disabled={busy} className="text-xs text-red-600 hover:underline disabled:opacity-40" data-testid="bulk-clear-btn">Svuota</button>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {files.map((f, i) => (
                <div key={f.name + i} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`bulk-file-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FolderArchive className="h-4 w-4 text-slate-500 shrink-0" />
                    <span className="truncate" title={f.webkitRelativePath || f.name}>{f.webkitRelativePath || f.name}</span>
                    <span className="text-xs text-slate-500 shrink-0">{fmtSize(f.size)}</span>
                  </div>
                  <button onClick={() => removeFile(i)} disabled={busy} className="text-slate-500 hover:text-red-600 disabled:opacity-40 shrink-0" data-testid={`bulk-remove-${i}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button onClick={analyze} disabled={busy || !files.length || !active} data-testid="bulk-analyze-btn"
          className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
          {phase === "preparing" ? "Caricamento file..." : phase === "analyzing" ? "Analisi AI in corso..." : "Analizza tutti"}
        </Button>

        {busy && (
          <div className="space-y-2" data-testid="bulk-progress">
            <Progress value={phase === "analyzing" ? pct : undefined} className="h-2" />
            <p className="text-xs text-slate-500">
              {phase === "preparing" ? "Estrazione del testo dai documenti..." : `Analisi ${progress.done}/${progress.total} documenti`}
            </p>
          </div>
        )}
      </Card>

      {skipped.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50/60" data-testid="bulk-skipped">
          <div className="flex items-center gap-2 mb-2 text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">{skipped.length} file scartati</span>
          </div>
          <ul className="text-xs text-amber-900/80 space-y-1">
            {skipped.map((s, i) => (
              <li key={i} className="flex items-center gap-2" data-testid={`bulk-skipped-${i}`}>
                <span className="font-mono">{s.filename}</span>
                <span className="text-amber-700/70">— {s.reason}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {items.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-display font-medium">Revisione ({toSave.length}/{items.length} da salvare)</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setApplyOpen((v) => !v)} data-testid="bulk-apply-toggle">
                <ListChecks className="h-4 w-4 mr-2" /> Assegna processi/requisiti
              </Button>
              <Button onClick={save} disabled={saving || !toSave.length} data-testid="bulk-save-btn"
                className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Salva selezionati
              </Button>
            </div>
          </div>

          {applyOpen && (
            <div className="p-4 border-b bg-blue-50/40 space-y-4" data-testid="bulk-apply-panel">
              <p className="text-xs text-slate-500">Applica gli stessi processi e requisiti a tutti i <strong>{toSave.length}</strong> documenti inclusi.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="label-caps mb-1.5">Processi</div>
                  <div className="rounded-md border border-slate-200 bg-white p-2 max-h-40 overflow-y-auto space-y-1">
                    {processes.length === 0 ? <p className="text-xs text-slate-500 p-1">Nessun processo</p> : processes.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm p-1 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={applyProcs.includes(p.id)} onCheckedChange={() => setApplyProcs((l) => toggleIn(l, p.id))} data-testid={`apply-proc-${p.id}`} />
                        {p.nome}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="label-caps mb-1.5">Schema</div>
                  <Select value={applyScheme || "none"} onValueChange={(v) => { setApplyScheme(v === "none" ? "" : v); setApplyReqs([]); }}>
                    <SelectTrigger data-testid="apply-scheme"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno</SelectItem>
                      {schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.sigla}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="label-caps mb-1.5">Requisiti</div>
                  <div className="rounded-md border border-slate-200 bg-white p-2 max-h-40 overflow-y-auto space-y-1">
                    {!applyScheme ? <p className="text-xs text-slate-500 p-1">Seleziona uno schema</p> : requirements.length === 0 ? <p className="text-xs text-slate-500 p-1">Nessun requisito</p> : requirements.map((r) => (
                      <label key={r.id} className="flex items-start gap-2 text-sm p-1 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={applyReqs.includes(r.id)} onCheckedChange={() => setApplyReqs((l) => toggleIn(l, r.id))} data-testid={`apply-req-${r.id}`} className="mt-0.5" />
                        <span>{r.codice} — {r.titolo}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <Button size="sm" onClick={applyToAll} data-testid="bulk-apply-btn"
                className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                Applica ai {toSave.length} documenti
              </Button>
            </div>
          )}

          <div className="divide-y divide-slate-100" data-testid="bulk-items">
            {items.map((it, i) => (
              <div key={i} className={it.error ? "bg-red-50/40" : ""} data-testid={`bulk-item-${i}`}>
                <div className="p-4 flex items-center gap-3">
                  <Checkbox checked={it.include} disabled={it.error} onCheckedChange={(v) => upd(i, { include: !!v })} />
                  <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_90px] gap-2 items-center">
                      <Input value={it.codice} onChange={(e) => upd(i, { codice: e.target.value })} placeholder="Codice" className="h-8 font-mono text-sm" data-testid={`bulk-codice-${i}`} />
                      <Input value={it.titolo} onChange={(e) => upd(i, { titolo: e.target.value })} placeholder="Titolo" className="h-8 text-sm" data-testid={`bulk-titolo-${i}`} />
                      <Select value={it.tipo} onValueChange={(v) => upd(i, { tipo: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{TIPI_DOCUMENTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 pl-0.5">
                      <Badge variant="outline" className={`text-[10px] uppercase font-mono ${EXT_BADGE[ext(it.filename)] || "text-slate-500"}`}>{ext(it.filename) || "file"}</Badge>
                      <span className="text-[11px] text-slate-500 truncate" title={it.filename} data-testid={`bulk-filename-${i}`}>{it.filename}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(it.process_ids?.length > 0 || it.requirement_ids?.length > 0) && (
                      <span className="text-[11px] text-slate-500 hidden md:inline">{it.process_ids?.length || 0}P · {it.requirement_ids?.length || 0}R</span>
                    )}
                    <button onClick={() => setPreviewIdx(previewIdx === i ? null : i)} disabled={!it.text_excerpt}
                      className="text-slate-500 hover:text-blue-600 disabled:opacity-30" data-testid={`bulk-preview-${i}`} title="Anteprima contenuto">
                      {previewIdx === i ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    {it.error ? (
                      <div className="flex items-center gap-1.5">
                        <Badge className="bg-red-100 text-red-700 border-red-200 gap-1"><AlertTriangle className="h-3 w-3" /> errore</Badge>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => retryItem(i)} disabled={retrying === i} data-testid={`bulk-retry-${i}`}>
                          {retrying === i ? <Loader2 className="h-3 w-3 animate-spin" /> : "Riprova"}
                        </Button>
                      </div>
                    ) : it.duplicate ? (
                      <Select value={it.dupAction} onValueChange={(v) => upd(i, { dupAction: v })}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`bulk-dup-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="skip">Salta (dup.)</SelectItem>
                          <SelectItem value="create">Crea comunque</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : <Badge variant="outline" className="text-[11px]">nuovo</Badge>}
                  </div>
                </div>
                {previewIdx === i && (
                  <div className="px-4 pb-4" data-testid={`bulk-preview-panel-${i}`}>
                    <div className="rounded-md border border-slate-200 bg-slate-50/70 p-3">
                      <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
                        <Wand2 className="h-3.5 w-3.5 text-blue-700" /> Anteprima contenuto estratto — {it.filename}
                      </div>
                      <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans max-h-56 overflow-y-auto">{it.text_excerpt || "Nessun testo estraibile da questo file."}</pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
